import { useState, useEffect } from 'react';
import { useLayoutPref } from '@/hooks/useLayoutPref';
import DirectoryOptInCard from '@/components/allottees/DirectoryOptInCard';
import { useAuth } from '@/context/AuthContext';
import { EditResidentFlat } from '@/components/EditResidentFlat';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { MemberTag, AuthorTag } from '@/components/ui/MemberTag';
import { CorpusRightsLink } from '@/components/corpus/CorpusLinks';
import ClaimFlatCard from '@/components/allottees/ClaimFlatCard';
import { AOP_META } from '@/components/allottees/aopMeta';
import { DirectoryHeader, DirectoryRow } from '@/components/allottees/DirectoryTable';
import { 
  Users, FileText, Bell, MessageSquare, Calendar,
  Plus, Upload, Mail, CheckCircle2, Clock, AlertCircle,
  Home, Phone, Briefcase, Search, Loader2, Building2,
  ChevronDown, Layers, ThumbsUp, Eye, X, Check,
  Send, Filter, ArrowRight, UserCheck, FileCheck,
  AlertTriangle, Archive, Edit
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// ==================== RESIDENTS DIRECTORY ====================
export function ResidentsDirectory({ projectId }) {
  const { user: viewer } = useAuth();
  const isAdminViewer = viewer?.role === 'admin';
  const [loading, setLoading] = useState(true);
  const [directory, setDirectory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterQualification, setFilterQualification] = useState('all');
  const [expandedWings, setExpandedWings] = useState({});
  const [selectedWing, setSelectedWing] = useState('all');
  const [filterAop, setFilterAop] = useState('all');
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    if (projectId) fetchDirectory();
  }, [projectId]);

  const fetchDirectory = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/buyer-sourced/${projectId}/residents-directory`);
      setDirectory(res.data);
      setDenied(false);
      const wings = {};
      res.data?.residents?.forEach(r => {
        const parsed = parseFlatNumber(r.flat_number);
        if (parsed.wing) wings[parsed.wing] = true;
      });
      setExpandedWings(wings);
    } catch (err) {
      setDenied([401, 403].includes(err?.response?.status));
      console.error('Failed to load residents directory:', err);
    } finally {
      setLoading(false);
    }
  };

  const parseFlatNumber = (flatNumber) => {
    if (!flatNumber || flatNumber === 'Not specified') {
      return { wing: 'Unassigned', floor: 0, unit: flatNumber || 'N/A', original: flatNumber };
    }
    
    const flat = flatNumber.trim().toUpperCase();
    let wing = 'Unassigned';
    let floor = 0;
    let unit = flatNumber;

    // Free-text tolerant: "W9 202", "W9-202", "Wing 9 - 202", "wing 9, Flat no 202", "Tower A 1203"
    const loose = flat.match(/^(?:W(?:ING)?|TOWER|T|BLDG|BUILDING)\s*[-–.:#]?\s*([A-Z]?\d+[A-Z]?|[A-Z])\b[^0-9]*?(\d{2,5})\b/);
    if (loose) {
      const w = loose[1];
      wing = `Wing ${w}`;
      const num = loose[2];
      floor = num.length >= 3 ? (parseInt(num.slice(0, -2)) || 0) : 0;
      return { wing, floor, unit: num, original: `W${w}-${num}` };
    }

    const pattern1 = flat.match(/^([A-Z]+\d*)\s*[-]?\s*(\d+)$/);
    if (pattern1) {
      wing = pattern1[1];
      const num = pattern1[2];
      if (num.length >= 3) {
        floor = parseInt(num.slice(0, -2)) || 0;
        unit = num.slice(-2);
      } else if (num.length === 2) {
        floor = parseInt(num[0]) || 0;
        unit = num;
      } else {
        unit = num;
      }
      return { wing, floor, unit: `${floor}${unit.padStart(2, '0')}`, original: flatNumber };
    }
    
    const pattern2 = flat.match(/^([A-Z])\s*[-]?\s*(\d+)$/);
    if (pattern2) {
      wing = `Wing ${pattern2[1]}`;
      const num = pattern2[2];
      if (num.length >= 3) {
        floor = parseInt(num.slice(0, -2)) || 0;
        unit = num.slice(-2);
      } else {
        floor = parseInt(num[0]) || 0;
        unit = num;
      }
      return { wing, floor, unit: num, original: flatNumber };
    }
    
    const pattern3 = flat.match(/^(\d+)$/);
    if (pattern3) {
      const num = pattern3[1];
      if (num.length >= 3) {
        floor = parseInt(num.slice(0, -2)) || 0;
        unit = num.slice(-2);
      }
      return { wing: 'Main', floor, unit: num, original: flatNumber };
    }
    
    return { wing: 'Unassigned', floor: 0, unit: flatNumber, original: flatNumber };
  };

  const getStatusBadge = (status, compact = false) => {
    if (compact) {
      switch (status) {
        case 'verified':
          return <span className="w-2 h-2 rounded-full bg-green-500 inline-block" title="Verified"></span>;
        case 'developer_verification_pending':
          return <span className="w-2 h-2 rounded-full bg-amber-500 inline-block" title="Pending Dev Verification"></span>;
        case 'pending':
          return <span className="w-2 h-2 rounded-full bg-blue-500 inline-block" title="Pending Review"></span>;
        default:
          return <span className="w-2 h-2 rounded-full bg-gray-300 inline-block" title="Not Verified"></span>;
      }
    }
    switch (status) {
      case 'verified':
        return <Badge className="bg-green-100 text-green-700 border-green-200 text-xs"><CheckCircle2 className="h-3 w-3 mr-1" />Verified</Badge>;
      case 'developer_verification_pending':
        return <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
      case 'pending':
        return <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-xs"><Clock className="h-3 w-3 mr-1" />Review</Badge>;
      default:
        return <Badge variant="outline" className="text-muted-foreground text-xs"><AlertCircle className="h-3 w-3 mr-1" />Unverified</Badge>;
    }
  };

  const filteredResidents = directory?.residents?.filter(r => {
    const matchesSearch = !searchTerm || 
      r.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.flat_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.qualification?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.occupation?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterStatus === 'all' || 
      (filterStatus === 'verified' && r.verification_status === 'verified') ||
      (filterStatus === 'pending' && ['pending', 'developer_verification_pending'].includes(r.verification_status)) ||
      (filterStatus === 'register' && r.verification_status === 'register') ||
      (filterStatus === 'unverified' && !['verified', 'pending', 'developer_verification_pending', 'register'].includes(r.verification_status));
    
    const matchesQualification = filterQualification === 'all' || r.qualification === filterQualification;
    const matchesAop = filterAop === 'all' || (filterAop === 'on_fbc' ? r.on_fbc : r.aop_status === filterAop);
    
    const parsed = parseFlatNumber(r.flat_number);
    const matchesWing = selectedWing === 'all' || parsed.wing === selectedWing;
    
    return matchesSearch && matchesFilter && matchesWing && matchesQualification && matchesAop;
  }) || [];

  const organizeByWingAndFloor = (residents) => {
    const organized = {};
    
    residents.forEach(resident => {
      const parsed = parseFlatNumber(resident.flat_number);
      const wing = parsed.wing;
      const floor = parsed.floor;
      
      if (!organized[wing]) {
        organized[wing] = { floors: {}, count: 0, verifiedCount: 0 };
      }
      if (!organized[wing].floors[floor]) {
        organized[wing].floors[floor] = [];
      }
      
      organized[wing].floors[floor].push({ ...resident, parsed });
      organized[wing].count++;
      if (resident.verification_status === 'verified') {
        organized[wing].verifiedCount++;
      }
    });
    
    Object.keys(organized).forEach(wing => {
      const sortedFloors = {};
      Object.keys(organized[wing].floors)
        .sort((a, b) => parseInt(a) - parseInt(b))
        .forEach(floor => {
          sortedFloors[floor] = organized[wing].floors[floor].sort((a, b) =>
            (parseInt(a.parsed.unit, 10) || 0) - (parseInt(b.parsed.unit, 10) || 0) || String(a.parsed.unit).localeCompare(String(b.parsed.unit)) || (a.holder_position || 99) - (b.holder_position || 99)
          );
        });
      organized[wing].floors = sortedFloors;
    });
    
    return organized;
  };

  const toggleWing = (wing) => {
    setExpandedWings(prev => ({ ...prev, [wing]: !prev[wing] }));
  };

  // Natural wing order: Wing 1, Wing 2, … Wing 10 (not lexicographic); lettered wings after; Unassigned last
  const wingSortKey = (w) => {
    const s = String(w || '');
    if (s === 'Unassigned') return 'zzzz';
    const m = s.match(/(\d+)/);
    if (m) return String(parseInt(m[1], 10)).padStart(4, '0') + '|' + s;
    return 'zzzy|' + s;
  };

  const getWings = () => {
    const wings = new Set();
    directory?.residents?.forEach(r => {
      const parsed = parseFlatNumber(r.flat_number);
      wings.add(parsed.wing);
    });
    return Array.from(wings).sort((a, b) => wingSortKey(a).localeCompare(wingSortKey(b)));
  };

  const getQualifications = () => {
    const quals = new Set();
    directory?.residents?.forEach(r => {
      if (r.qualification) quals.add(r.qualification);
    });
    return Array.from(quals).sort();
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!directory || !directory.residents?.length) {
    return (
      <div className="space-y-4">
        <ClaimFlatCard projectId={projectId} onChanged={fetchDirectory} />
        {directory && <DirectoryOptInCard projectId={projectId} directory={directory} onChanged={fetchDirectory} />}
        <Card className="border-dashed">
          <CardContent className="py-6 text-center text-muted-foreground" data-testid="directory-empty">
            <Users className="h-10 w-10 mx-auto mb-3 opacity-30" />
            {denied ? (
              <>
                <p>The Members Directory is open to verified members of this project.</p>
                <p className="text-xs mt-1">Claim your flat above, or verify your ownership, to see fellow allottees.</p>
              </>
            ) : (
              <>
                <p>No members have chosen to be listed yet.</p>
                <p className="text-xs mt-1">Owners appear here only after they join FBC and choose to be listed.</p>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  const organizedData = organizeByWingAndFloor(filteredResidents);
  const wings = getWings();
  const qualifications = getQualifications();
  const cols = directory.columns || {};

  return (
    <div className="space-y-4">
      <ClaimFlatCard projectId={projectId} onChanged={fetchDirectory} />
      <DirectoryOptInCard projectId={projectId} directory={directory} onChanged={fetchDirectory} />
      {/* Stats */}
      <div className="grid grid-cols-3 gap-2">
        <Card className="p-3 text-center">
          <div className="text-2xl font-bold">{directory.residents.filter((r) => directory.is_admin_view || !r.is_me || r.listed).length}</div>
          <div className="text-xs text-muted-foreground">Listed</div>
        </Card>
        <Card className="p-3 text-center bg-green-50 dark:bg-green-900/20">
          <div className="text-2xl font-bold text-green-600">{directory.verified_count}</div>
          <div className="text-xs text-green-600">On FBC</div>
        </Card>
        <Card className="p-3 text-center bg-amber-50 dark:bg-amber-900/20">
          <div className="text-2xl font-bold text-amber-600">{directory.is_admin_view ? directory.pending_count : directory.hidden_count}</div>
          <div className="text-xs text-amber-600">{directory.is_admin_view ? 'Pending' : 'Not listed'}</div>
        </Card>
      </div>
      {directory.register_total > 0 && (
        <p className="text-xs text-muted-foreground" data-testid="directory-aop-summary">
          {directory.is_admin_view ? `Admin view: includes ${directory.register_count} register owners who haven't joined FBC or opted in (members don't see them) · ` : ''}Whole register ·
          AOP: {directory.aop_counts.yes} Yes · {directory.aop_counts.no} No · {directory.aop_counts.unconfirmed} Unconfirmed.
          AOP membership and FBC registration are separate.
        </p>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, flat, qualification..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
            data-testid="residents-search"
          />
        </div>
        <select
          value={selectedWing}
          onChange={(e) => setSelectedWing(e.target.value)}
          className="px-3 py-2 border rounded-md text-sm bg-background"
          data-testid="residents-wing-filter"
        >
          <option value="all">All Wings</option>
          {wings.map(wing => (
            <option key={wing} value={wing}>{wing}</option>
          ))}
        </select>
        <select
          value={filterQualification}
          onChange={(e) => setFilterQualification(e.target.value)}
          className="px-3 py-2 border rounded-md text-sm bg-background"
          data-testid="residents-qual-filter"
        >
          <option value="all">All Professions</option>
          {qualifications.map(qual => (
            <option key={qual} value={qual}>{qual}</option>
          ))}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-2 border rounded-md text-sm bg-background"
          data-testid="residents-filter"
        >
          <option value="all">All Status</option>
          <option value="verified">Verified</option>
          <option value="pending">Pending</option>
          <option value="register">In register, not on FBC</option>
          <option value="unverified">Unverified</option>
        </select>
        {directory.register_count > 0 && (
          <select value={filterAop} onChange={(e) => setFilterAop(e.target.value)} className="px-3 py-2 border rounded-md text-sm bg-background max-w-full sm:max-w-[220px]" data-testid="residents-aop-filter">
            <option value="all">All AOP statuses</option>
            {Object.entries(AOP_META).map(([k, m]) => <option key={k} value={k}>{m.long}</option>)}
            <option value="on_fbc">On FBC</option>
          </select>
        )}
      </div>

      {/* Directory List */}
      <div className="space-y-3 max-h-[500px] overflow-y-auto">
        {Object.keys(organizedData).sort((a, b) => wingSortKey(a).localeCompare(wingSortKey(b))).map(wing => (
          <Card key={wing} className="overflow-hidden" data-testid={`wing-${wing}`}>
            <div 
              className="flex items-center justify-between gap-2 p-3 bg-muted/50 cursor-pointer hover:bg-muted/70 transition-colors"
              onClick={() => toggleWing(wing)}
            >
              <div className="flex items-center gap-2 min-w-0">
                <Building2 className="h-4 w-4 text-primary shrink-0" />
                <span className="font-semibold whitespace-nowrap">{wing}</span>
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  · {organizedData[wing].count} {organizedData[wing].count === 1 ? 'resident' : 'residents'} · {organizedData[wing].verifiedCount} verified
                </span>
              </div>
              <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${expandedWings[wing] ? 'rotate-180' : ''}`} />
            </div>
            
            {expandedWings[wing] && (
              <div className="divide-y">
                <DirectoryHeader cols={cols} />
                {Object.values(organizedData[wing].floors).flat().map(resident => (
                  <DirectoryRow key={resident.id} r={resident} cols={cols}
                    actions={isAdminViewer && resident.membership_id ? <EditResidentFlat resident={resident} onSaved={fetchDirectory} /> : null}
                    statusDot={resident.verification_status !== 'register' ? getStatusBadge(resident.verification_status, true) : <span className="w-2 h-2 rounded-full bg-gray-300 inline-block" title="In register, not on FBC" />} />
                ))}
              </div>
            )}
          </Card>
        ))}
        
        {Object.keys(organizedData).length === 0 && (
          <p className="text-center text-muted-foreground py-4 text-sm">
            No residents match your search.
          </p>
        )}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 text-xs text-muted-foreground pt-2 border-t">
        <span className="font-medium">Status:</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500"></span> Verified</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500"></span> Pending</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-gray-300"></span> Unverified</span>
      </div>
    </div>
  );
}


// ==================== CORRESPONDENCE REPOSITORY ====================
export function CorrespondenceRepository({ projectId, isAdmin, topic }) {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [correspondence, setCorrespondence] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingItems, setPendingItems] = useState([]);
  const [showPending, setShowPending] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [suggestDialogOpen, setSuggestDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    type: 'letter',
    direction: 'sent',
    subject: '',
    notes: ''
  });

  useEffect(() => {
    if (projectId) fetchCorrespondence();
  }, [projectId]);

  const fetchCorrespondence = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/buyers-group/${projectId}/correspondence`, {
        params: topic ? { topic } : undefined,
        headers: { Authorization: `Bearer ${token}` }
      });
      setCorrespondence(res.data.correspondence || []);
      setPendingCount(res.data.pending_count || 0);
    } catch (err) {
      console.error('Failed to load correspondence:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPending = async () => {
    try {
      const res = await axios.get(`${API}/api/buyers-group/${projectId}/correspondence/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingItems(res.data || []);
      setShowPending(true);
    } catch (err) {
      toast.error('Failed to load pending items');
    }
  };

  const handleSubmit = async (isDirect = true) => {
    if (!formData.subject.trim()) {
      toast.error('Subject is required');
      return;
    }
    try {
      const endpoint = isDirect 
        ? `${API}/api/buyers-group/${projectId}/correspondence`
        : `${API}/api/buyers-group/${projectId}/correspondence/suggest`;
      
      await axios.post(endpoint, topic ? { ...formData, topics: [topic] } : formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(isDirect ? 'Correspondence added' : 'Suggestion submitted for approval');
      setDialogOpen(false);
      setSuggestDialogOpen(false);
      setFormData({ date: new Date().toISOString().split('T')[0], type: 'letter', direction: 'sent', subject: '', notes: '' });
      fetchCorrespondence();
    } catch (err) {
      toast.error('Failed to submit');
    }
  };

  const handleReview = async (corrId, action, reason = '') => {
    try {
      await axios.post(
        `${API}/api/buyers-group/${projectId}/correspondence/${corrId}/review?action=${action}${reason ? `&rejection_reason=${encodeURIComponent(reason)}` : ''}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Correspondence ${action}d`);
      fetchCorrespondence();
      fetchPending();
    } catch (err) {
      toast.error('Failed to review');
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'email': return <Mail className="h-4 w-4" />;
      case 'legal_notice': return <AlertTriangle className="h-4 w-4 text-red-500" />;
      case 'rti': return <FileCheck className="h-4 w-4 text-blue-500" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  if (loading) {
    return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">{correspondence.length} documents</span>
          {isAdmin && pendingCount > 0 && (
            <Button variant="outline" size="sm" onClick={fetchPending}>
              <Clock className="h-3 w-3 mr-1" /> {pendingCount} Pending
            </Button>
          )}
        </div>
        <div className="flex gap-2">
          {isAdmin ? (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm"><Plus className="h-4 w-4 mr-1" /> Add</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Add Correspondence</DialogTitle></DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Date</Label>
                      <Input type="date" value={formData.date} onChange={(e) => setFormData(p => ({ ...p, date: e.target.value }))} />
                    </div>
                    <div>
                      <Label>Type</Label>
                      <Select value={formData.type} onValueChange={(v) => setFormData(p => ({ ...p, type: v }))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="letter">Letter</SelectItem>
                          <SelectItem value="email">Email</SelectItem>
                          <SelectItem value="legal_notice">Legal Notice</SelectItem>
                          <SelectItem value="rti">RTI</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label>Direction</Label>
                    <Select value={formData.direction} onValueChange={(v) => setFormData(p => ({ ...p, direction: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sent">Sent to Developer</SelectItem>
                        <SelectItem value="received">Received from Developer</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Subject</Label>
                    <Input value={formData.subject} onChange={(e) => setFormData(p => ({ ...p, subject: e.target.value }))} placeholder="Brief subject..." />
                  </div>
                  <div>
                    <Label>Notes (Optional)</Label>
                    <Textarea value={formData.notes} onChange={(e) => setFormData(p => ({ ...p, notes: e.target.value }))} rows={2} />
                  </div>
                  <Button onClick={() => handleSubmit(true)} className="w-full">Add Correspondence</Button>
                </div>
              </DialogContent>
            </Dialog>
          ) : (
            <Dialog open={suggestDialogOpen} onOpenChange={setSuggestDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline"><Send className="h-4 w-4 mr-1" /> Suggest</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Suggest Correspondence</DialogTitle></DialogHeader>
                <p className="text-sm text-muted-foreground">Your suggestion will be reviewed by the group admin.</p>
                <div className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Date</Label>
                      <Input type="date" value={formData.date} onChange={(e) => setFormData(p => ({ ...p, date: e.target.value }))} />
                    </div>
                    <div>
                      <Label>Type</Label>
                      <Select value={formData.type} onValueChange={(v) => setFormData(p => ({ ...p, type: v }))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="letter">Letter</SelectItem>
                          <SelectItem value="email">Email</SelectItem>
                          <SelectItem value="legal_notice">Legal Notice</SelectItem>
                          <SelectItem value="rti">RTI</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label>Direction</Label>
                    <Select value={formData.direction} onValueChange={(v) => setFormData(p => ({ ...p, direction: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sent">Sent to Developer</SelectItem>
                        <SelectItem value="received">Received from Developer</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Subject</Label>
                    <Input value={formData.subject} onChange={(e) => setFormData(p => ({ ...p, subject: e.target.value }))} placeholder="Brief subject..." />
                  </div>
                  <Button onClick={() => handleSubmit(false)} className="w-full">Submit for Approval</Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Pending Items Modal */}
      {showPending && (
        <Card className="border-amber-200 bg-amber-50 dark:bg-amber-900/20">
          <CardHeader className="py-3">
            <div className="flex justify-between items-center">
              <CardTitle className="text-sm">Pending Approvals</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowPending(false)}><X className="h-4 w-4" /></Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {pendingItems.map(item => (
              <div key={item.id} className="flex items-center justify-between p-2 bg-background rounded border">
                <div>
                  <div className="font-medium text-sm">{item.subject}</div>
                  <div className="text-xs text-muted-foreground">
                    Suggested by {item.suggested_by_name} • {item.type} • {item.direction}
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" className="text-green-600" onClick={() => handleReview(item.id, 'approve')}>
                    <Check className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleReview(item.id, 'reject', 'Does not meet guidelines')}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
            {pendingItems.length === 0 && <p className="text-sm text-muted-foreground text-center py-2">No pending items</p>}
          </CardContent>
        </Card>
      )}

      {/* Correspondence List */}
      <div className="space-y-2">
        {correspondence.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-6 text-center text-muted-foreground">
              <Mail className="h-8 w-8 mx-auto mb-2 opacity-30" />
              No correspondence recorded yet.
            </CardContent>
          </Card>
        ) : (
          correspondence.map(item => (
            <Card key={item.id} className="p-3">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-muted rounded">{getTypeIcon(item.type)}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">{item.subject}</span>
                    <Badge variant={item.direction === 'sent' ? 'default' : 'secondary'} className="text-xs">
                      {item.direction === 'sent' ? 'Sent' : 'Received'}
                    </Badge>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {new Date(item.date).toLocaleDateString()} • {item.type.replace('_', ' ')}
                    {item.notes && <span className="block mt-1 italic">{item.notes}</span>}
                  </div>
                </div>
                <Badge variant="outline" className="text-xs shrink-0">
                  {item.response_status === 'received' ? 'Response Received' : 
                   item.response_status === 'no_response' ? 'No Response' : 'Awaiting'}
                </Badge>
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}


// ==================== ISSUE THREADS ====================
export function IssueThreads({ projectId, isAdmin, subcategories }) {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [category, setCategory] = useState('all');
  const [newThreadDialog, setNewThreadDialog] = useState(false);
  const [contributionText, setContributionText] = useState('');
  const [threadDetail, setThreadDetail] = useState(null);
  const [pendingContribs, setPendingContribs] = useState([]);
  const [newThread, setNewThread] = useState({ title: '', description: '', category: 'pre_possession', subcategory: subcategories?.[0] || 'custom' });

  useEffect(() => {
    if (projectId) fetchThreads();
  }, [projectId, category]);

  const fetchThreads = async () => {
    setLoading(true);
    try {
      const url = category === 'all' 
        ? `${API}/api/buyers-group/${projectId}/threads`
        : `${API}/api/buyers-group/${projectId}/threads?category=${category}`;
      const res = await axios.get(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      setThreads((res.data || []).filter((t) => !subcategories || subcategories.includes(t.subcategory)));
    } catch (err) {
      console.error('Failed to load threads:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchThreadDetail = async (threadId) => {
    try {
      const res = await axios.get(`${API}/api/buyers-group/${projectId}/threads/${threadId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setThreadDetail(res.data);
      setSelectedThread(threadId);
      
      if (isAdmin) {
        const pendingRes = await axios.get(`${API}/api/buyers-group/${projectId}/threads/${threadId}/pending`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPendingContribs(pendingRes.data || []);
      }
    } catch (err) {
      toast.error('Failed to load thread');
    }
  };

  const handleEndorse = async (threadId) => {
    if (!token) {
      toast.error('Please login to endorse');
      return;
    }
    try {
      const res = await axios.post(`${API}/api/buyers-group/${projectId}/threads/${threadId}/endorse`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      fetchThreads();
      if (selectedThread === threadId) fetchThreadDetail(threadId);
    } catch (err) {
      toast.error('Failed to endorse');
    }
  };

  const handleSubmitContribution = async () => {
    if (!contributionText.trim()) {
      toast.error('Please enter your contribution');
      return;
    }
    try {
      await axios.post(`${API}/api/buyers-group/${projectId}/threads/${selectedThread}/contributions`, 
        { content: contributionText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Contribution submitted for admin approval');
      setContributionText('');
      fetchThreadDetail(selectedThread);
    } catch (err) {
      toast.error('Failed to submit contribution');
    }
  };

  const handleReviewContribution = async (contribId, status, reason = '') => {
    try {
      await axios.post(`${API}/api/buyers-group/${projectId}/threads/${selectedThread}/contributions/${contribId}/review`,
        { status, rejection_reason: reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Contribution ${status}`);
      fetchThreadDetail(selectedThread);
    } catch (err) {
      toast.error('Failed to review');
    }
  };

  const handleCreateThread = async () => {
    if (!newThread.title.trim() || !newThread.description.trim()) {
      toast.error('Title and description required');
      return;
    }
    try {
      await axios.post(`${API}/api/buyers-group/${projectId}/threads`, newThread, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Thread created');
      setNewThreadDialog(false);
      setNewThread({ title: '', description: '', category: 'pre_possession', subcategory: subcategories?.[0] || 'custom' });
      fetchThreads();
    } catch (err) {
      toast.error('Failed to create thread');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  // Thread Detail View
  if (selectedThread && threadDetail) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => { setSelectedThread(null); setThreadDetail(null); }}>
          <ArrowRight className="h-4 w-4 mr-1 rotate-180" /> Back to Threads
        </Button>

        {/* Corpus law, method & calculator live once, in Post-Possession Rights → Corpus Fund */}
        {threadDetail.category === 'corpus_protection' && <CorpusRightsLink testid="thread-corpus-rights-link" />}

        <Card>
          <CardHeader>
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="text-lg">{threadDetail.title}</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">{threadDetail.description}</p>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={threadDetail.status === 'open' ? 'default' : 'secondary'}>
                  {threadDetail.status}
                </Badge>
                <Button 
                  variant={threadDetail.user_endorsed ? 'default' : 'outline'} 
                  size="sm"
                  onClick={() => handleEndorse(selectedThread)}
                >
                  <ThumbsUp className="h-4 w-4 mr-1" /> {threadDetail.endorsement_count}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Pending Contributions (Admin) */}
            {isAdmin && pendingContribs.length > 0 && (
              <Card className="border-amber-200 bg-amber-50 dark:bg-amber-900/20">
                <CardHeader className="py-2">
                  <CardTitle className="text-sm">Pending Approval ({pendingContribs.length})</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {pendingContribs.map(contrib => (
                    <div key={contrib.id} className="p-3 bg-background rounded border">
                      <AuthorTag 
                        name={contrib.author_name}
                        flatNumber={contrib.author_flat_number}
                        wing={contrib.author_wing}
                        status={contrib.author_verification_status || 'unverified'}
                        timestamp={contrib.created_at}
                      />
                      <p className="text-sm mt-2">{contrib.content}</p>
                      <div className="flex justify-end items-center mt-2 gap-1">
                        <Button size="sm" variant="ghost" className="text-green-600" onClick={() => handleReviewContribution(contrib.id, 'approved')}>
                          <Check className="h-4 w-4 mr-1" /> Approve
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleReviewContribution(contrib.id, 'rejected', 'Does not meet guidelines')}>
                          <X className="h-4 w-4 mr-1" /> Reject
                        </Button>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Approved Contributions */}
            <div className="space-y-3">
              <h4 className="font-medium text-sm">Thread History ({threadDetail.contributions?.length || 0} contributions)</h4>
              {threadDetail.contributions?.length === 0 ? (
                <p className="text-sm text-muted-foreground italic">No contributions yet. Be the first to contribute!</p>
              ) : (
                threadDetail.contributions.map((contrib, idx) => (
                  <Card key={contrib.id} className="p-3">
                    <div className="flex items-start gap-2">
                      <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium shrink-0">
                        {idx + 1}
                      </div>
                      <div className="flex-1">
                        <AuthorTag 
                          name={contrib.author_name}
                          flatNumber={contrib.author_flat_number}
                          wing={contrib.author_wing}
                          status={contrib.author_verification_status || 'unverified'}
                          timestamp={contrib.created_at}
                          isAdmin={contrib.is_admin}
                        />
                        <p className="text-sm mt-2">{contrib.content}</p>
                      </div>
                    </div>
                  </Card>
                ))
              )}
            </div>

            {/* Add Contribution */}
            {user && threadDetail.status === 'open' && (
              <div className="border-t pt-4">
                <Label>Add Your Contribution</Label>
                <Textarea 
                  value={contributionText}
                  onChange={(e) => setContributionText(e.target.value)}
                  placeholder="Share relevant information, updates, or insights..."
                  rows={3}
                  className="mt-2"
                />
                <div className="flex justify-between items-center mt-2">
                  <p className="text-xs text-muted-foreground">Your contribution will be reviewed by admin before appearing.</p>
                  <Button onClick={handleSubmitContribution}>
                    <Send className="h-4 w-4 mr-1" /> Submit
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Thread List View
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className={`flex gap-2 flex-wrap ${subcategories ? "hidden" : ""}`}>
          <Button variant={category === 'all' ? 'default' : 'outline'} size="sm" onClick={() => setCategory('all')}>All</Button>
          <Button variant={category === 'pre_possession' ? 'default' : 'outline'} size="sm" onClick={() => setCategory('pre_possession')}>Pre-Possession</Button>
          <Button variant={category === 'post_possession' ? 'default' : 'outline'} size="sm" onClick={() => setCategory('post_possession')}>Post-Possession</Button>
          <Button
            variant={category === 'corpus_protection' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setCategory('corpus_protection')}
            data-testid="thread-filter-corpus"
            className={category === 'corpus_protection' ? '' : 'border-amber-300 text-amber-800 hover:bg-amber-50 dark:border-amber-700 dark:text-amber-300'}
          >
            Corpus Protection
          </Button>
        </div>
        {isAdmin && (
          <Dialog open={newThreadDialog} onOpenChange={setNewThreadDialog}>
            <DialogTrigger asChild>
              <Button size="sm"><Plus className="h-4 w-4 mr-1" /> New Thread</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Create Custom Thread</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label>Category</Label>
                  <Select value={newThread.category} onValueChange={(v) => setNewThread(p => ({ ...p, category: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pre_possession">Pre-Possession</SelectItem>
                      <SelectItem value="post_possession">Post-Possession</SelectItem>
                      <SelectItem value="corpus_protection">Corpus Protection</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Title</Label>
                  <Input value={newThread.title} onChange={(e) => setNewThread(p => ({ ...p, title: e.target.value }))} placeholder="Thread title..." />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea value={newThread.description} onChange={(e) => setNewThread(p => ({ ...p, description: e.target.value }))} rows={3} placeholder="What is this thread about?" />
                </div>
                <Button onClick={handleCreateThread} className="w-full">Create Thread</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="space-y-2">
        {threads.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-6 text-center text-muted-foreground">
              <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-30" />
              No threads found.
            </CardContent>
          </Card>
        ) : (
          threads.map(thread => (
            <Card 
              key={thread.id} 
              className="p-3 cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={() => fetchThreadDetail(thread.id)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">{thread.title}</span>
                    <Badge variant="outline" className="text-xs">
                      {thread.category === 'pre_possession' ? 'Pre'
                        : thread.category === 'post_possession' ? 'Post'
                        : thread.category === 'corpus_protection' ? 'Corpus'
                        : thread.category}
                    </Badge>
                    {thread.status !== 'open' && (
                      <Badge variant="secondary" className="text-xs">{thread.status}</Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{thread.description}</p>
                  <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <MessageSquare className="h-3 w-3" /> {thread.contribution_count || 0}
                    </span>
                    {thread.pending_count > 0 && isAdmin && (
                      <span className="flex items-center gap-1 text-amber-600">
                        <Clock className="h-3 w-3" /> {thread.pending_count} pending
                      </span>
                    )}
                  </div>
                </div>
                <Button 
                  variant={thread.user_endorsed ? 'default' : 'outline'} 
                  size="sm"
                  onClick={(e) => { e.stopPropagation(); handleEndorse(thread.id); }}
                >
                  <ThumbsUp className="h-3 w-3 mr-1" /> {thread.endorsement_count || 0}
                </Button>
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}


// ==================== NOTICES & AGENDA ====================
export function NoticesAgenda({ projectId, isAdmin, topic }) {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [notices, setNotices] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [agendaItems, setAgendaItems] = useState(['']);
  const [formData, setFormData] = useState({
    title: '',
    date_time: '',
    venue: ''
  });

  useEffect(() => {
    if (projectId) fetchNotices();
  }, [projectId]);

  const fetchNotices = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/buyers-group/${projectId}/notices`, {
        params: topic ? { topic } : undefined,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setNotices(res.data || []);
    } catch (err) {
      console.error('Failed to load notices:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.title.trim() || !formData.date_time || !formData.venue.trim()) {
      toast.error('All fields are required');
      return;
    }
    try {
      await axios.post(`${API}/api/buyers-group/${projectId}/notices`, {
        ...formData,
        ...(topic ? { topics: [topic] } : {}),
        agenda_items: agendaItems.filter(a => a.trim())
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success('Notice published');
      setDialogOpen(false);
      setFormData({ title: '', date_time: '', venue: '' });
      setAgendaItems(['']);
      fetchNotices();
    } catch (err) {
      toast.error('Failed to publish notice');
    }
  };

  const handleRsvp = async (noticeId, attending) => {
    try {
      await axios.post(`${API}/api/buyers-group/${projectId}/notices/${noticeId}/rsvp?attending=${attending}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('RSVP recorded');
      fetchNotices();
    } catch (err) {
      toast.error('Failed to RSVP');
    }
  };

  const addAgendaItem = () => setAgendaItems([...agendaItems, '']);
  const updateAgendaItem = (idx, value) => {
    const updated = [...agendaItems];
    updated[idx] = value;
    setAgendaItems(updated);
  };
  const removeAgendaItem = (idx) => setAgendaItems(agendaItems.filter((_, i) => i !== idx));

  if (loading) {
    return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <span className="text-sm text-muted-foreground">{notices.length} notices</span>
        {isAdmin && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm"><Plus className="h-4 w-4 mr-1" /> New Notice</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>Publish Notice with Agenda</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-4 max-h-[60vh] overflow-y-auto">
                <div>
                  <Label>Meeting Title</Label>
                  <Input value={formData.title} onChange={(e) => setFormData(p => ({ ...p, title: e.target.value }))} placeholder="e.g., Monthly Buyers' Meeting - March 2026" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Date & Time</Label>
                    <Input type="datetime-local" value={formData.date_time} onChange={(e) => setFormData(p => ({ ...p, date_time: e.target.value }))} />
                  </div>
                  <div>
                    <Label>Venue / Mode</Label>
                    <Input value={formData.venue} onChange={(e) => setFormData(p => ({ ...p, venue: e.target.value }))} placeholder="Community Hall / Zoom link" />
                  </div>
                </div>
                <div>
                  <Label>Agenda Items</Label>
                  <div className="space-y-2 mt-2">
                    {agendaItems.map((item, idx) => (
                      <div key={`agenda-${idx}`} className="flex gap-2">
                        <span className="w-6 h-9 flex items-center justify-center text-sm text-muted-foreground">{idx + 1}.</span>
                        <Input 
                          value={item} 
                          onChange={(e) => updateAgendaItem(idx, e.target.value)} 
                          placeholder="Agenda item..."
                        />
                        {agendaItems.length > 1 && (
                          <Button variant="ghost" size="sm" onClick={() => removeAgendaItem(idx)}>
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                    <Button variant="outline" size="sm" onClick={addAgendaItem}>
                      <Plus className="h-4 w-4 mr-1" /> Add Item
                    </Button>
                  </div>
                </div>
                <Button onClick={handleSubmit} className="w-full">Publish Notice</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="space-y-3">
        {notices.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-6 text-center text-muted-foreground">
              <Bell className="h-8 w-8 mx-auto mb-2 opacity-30" />
              No notices published yet.
            </CardContent>
          </Card>
        ) : (
          notices.map(notice => {
            const userRsvp = notice.rsvp_list?.find(r => r.user_id === user?.id);
            const attendingCount = notice.rsvp_list?.filter(r => r.attending).length || 0;
            
            return (
              <Card key={notice.id} className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-medium">{notice.title}</h4>
                    <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {new Date(notice.date_time).toLocaleString()}
                      </span>
                      <span>{notice.venue}</span>
                    </div>
                  </div>
                  <Badge variant="outline">{attendingCount} attending</Badge>
                </div>
                
                {notice.agenda_items?.length > 0 && (
                  <div className="mt-3 p-3 bg-muted/50 rounded">
                    <h5 className="font-medium text-sm mb-2">Agenda</h5>
                    <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
                      {notice.agenda_items.map((item, idx) => (
                        <li key={`us-item-${idx}`}>{item}</li>
                      ))}
                    </ol>
                  </div>
                )}
                
                {user && (
                  <div className="mt-3 pt-3 border-t flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Will you attend?</span>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant={userRsvp?.attending === true ? 'default' : 'outline'}
                        onClick={() => handleRsvp(notice.id, true)}
                      >
                        <Check className="h-4 w-4 mr-1" /> Yes
                      </Button>
                      <Button 
                        size="sm" 
                        variant={userRsvp?.attending === false ? 'destructive' : 'outline'}
                        onClick={() => handleRsvp(notice.id, false)}
                      >
                        <X className="h-4 w-4 mr-1" /> No
                      </Button>
                    </div>
                  </div>
                )}
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
}


// ==================== MEETINGS & MINUTES ====================
export function MeetingsMinutes({ projectId, isAdmin, topic }) {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [meetings, setMeetings] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [decisions, setDecisions] = useState(['']);
  const [actionItems, setActionItems] = useState([{ task: '', owner: '', deadline: '' }]);
  const [formData, setFormData] = useState({
    title: '',
    date: new Date().toISOString().split('T')[0],
    attendees_count: 0,
    minutes_summary: ''
  });

  useEffect(() => {
    if (projectId) fetchMeetings();
  }, [projectId]);

  const fetchMeetings = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/buyers-group/${projectId}/meetings`, {
        params: topic ? { topic } : undefined,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setMeetings(res.data || []);
    } catch (err) {
      console.error('Failed to load meetings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.title.trim() || !formData.minutes_summary.trim()) {
      toast.error('Title and minutes are required');
      return;
    }
    try {
      await axios.post(`${API}/api/buyers-group/${projectId}/meetings`, {
        ...formData,
        ...(topic ? { topics: [topic] } : {}),
        decisions: decisions.filter(d => d.trim()),
        action_items: actionItems.filter(a => a.task.trim())
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success('Meeting minutes saved');
      setDialogOpen(false);
      setFormData({ title: '', date: new Date().toISOString().split('T')[0], attendees_count: 0, minutes_summary: '' });
      setDecisions(['']);
      setActionItems([{ task: '', owner: '', deadline: '' }]);
      fetchMeetings();
    } catch (err) {
      toast.error('Failed to save meeting');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <span className="text-sm text-muted-foreground">{meetings.length} meetings recorded</span>
        {isAdmin && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm"><Plus className="h-4 w-4 mr-1" /> Add Minutes</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[80dvh] overflow-y-auto">
              <DialogHeader><DialogTitle>Record Meeting Minutes</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2">
                    <Label>Meeting Title</Label>
                    <Input value={formData.title} onChange={(e) => setFormData(p => ({ ...p, title: e.target.value }))} />
                  </div>
                  <div>
                    <Label>Date</Label>
                    <Input type="date" value={formData.date} onChange={(e) => setFormData(p => ({ ...p, date: e.target.value }))} />
                  </div>
                </div>
                <div>
                  <Label>Attendees Count</Label>
                  <Input type="number" value={formData.attendees_count} onChange={(e) => setFormData(p => ({ ...p, attendees_count: parseInt(e.target.value) || 0 }))} />
                </div>
                <div>
                  <Label>Minutes Summary</Label>
                  <Textarea value={formData.minutes_summary} onChange={(e) => setFormData(p => ({ ...p, minutes_summary: e.target.value }))} rows={4} />
                </div>
                <div>
                  <Label>Key Decisions</Label>
                  <div className="space-y-2 mt-2">
                    {decisions.map((d, idx) => (
                      <div key={`us-item-${idx}`} className="flex gap-2">
                        <Input value={d} onChange={(e) => { const u = [...decisions]; u[idx] = e.target.value; setDecisions(u); }} placeholder="Decision..." />
                        {decisions.length > 1 && <Button variant="ghost" size="sm" onClick={() => setDecisions(decisions.filter((_, i) => i !== idx))}><X className="h-4 w-4" /></Button>}
                      </div>
                    ))}
                    <Button variant="outline" size="sm" onClick={() => setDecisions([...decisions, ''])}><Plus className="h-4 w-4 mr-1" /> Add</Button>
                  </div>
                </div>
                <div>
                  <Label>Action Items</Label>
                  <div className="space-y-2 mt-2">
                    {actionItems.map((item, idx) => (
                      <div key={`us-item-${idx}`} className="grid grid-cols-4 gap-2">
                        <Input className="col-span-2" value={item.task} onChange={(e) => { const u = [...actionItems]; u[idx].task = e.target.value; setActionItems(u); }} placeholder="Task..." />
                        <Input value={item.owner} onChange={(e) => { const u = [...actionItems]; u[idx].owner = e.target.value; setActionItems(u); }} placeholder="Owner" />
                        <div className="flex gap-1">
                          <Input type="date" value={item.deadline} onChange={(e) => { const u = [...actionItems]; u[idx].deadline = e.target.value; setActionItems(u); }} />
                          {actionItems.length > 1 && <Button variant="ghost" size="sm" onClick={() => setActionItems(actionItems.filter((_, i) => i !== idx))}><X className="h-4 w-4" /></Button>}
                        </div>
                      </div>
                    ))}
                    <Button variant="outline" size="sm" onClick={() => setActionItems([...actionItems, { task: '', owner: '', deadline: '' }])}><Plus className="h-4 w-4 mr-1" /> Add</Button>
                  </div>
                </div>
                <Button onClick={handleSubmit} className="w-full">Save Minutes</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="space-y-3">
        {meetings.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-6 text-center text-muted-foreground">
              <Calendar className="h-8 w-8 mx-auto mb-2 opacity-30" />
              No meetings recorded yet.
            </CardContent>
          </Card>
        ) : (
          meetings.map(meeting => (
            <Accordion type="single" collapsible key={meeting.id}>
              <AccordionItem value={meeting.id} className="border rounded">
                <AccordionTrigger className="px-4 py-3 hover:no-underline">
                  <div className="flex items-center gap-3 text-left">
                    <Calendar className="h-4 w-4 text-primary" />
                    <div>
                      <span className="font-medium">{meeting.title}</span>
                      <span className="text-xs text-muted-foreground ml-2">
                        {new Date(meeting.date).toLocaleDateString()} • {meeting.attendees_count} attendees
                      </span>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4">
                  <div className="space-y-4">
                    <div>
                      <h5 className="font-medium text-sm mb-1">Minutes Summary</h5>
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap">{meeting.minutes_summary}</p>
                    </div>
                    {meeting.decisions?.length > 0 && (
                      <div>
                        <h5 className="font-medium text-sm mb-1">Key Decisions</h5>
                        <ul className="list-disc list-inside text-sm text-muted-foreground">
                          {meeting.decisions.map((d, i) => <li key={`us-sub-${i}`}>{d}</li>)}
                        </ul>
                      </div>
                    )}
                    {meeting.action_items?.length > 0 && (
                      <div>
                        <h5 className="font-medium text-sm mb-2">Action Items</h5>
                        <div className="border rounded overflow-hidden">
                          <table className="w-full text-sm">
                            <thead className="bg-muted">
                              <tr>
                                <th className="text-left p-2">Task</th>
                                <th className="text-left p-2">Owner</th>
                                <th className="text-left p-2">Deadline</th>
                              </tr>
                            </thead>
                            <tbody>
                              {meeting.action_items.map((item, i) => (
                                <tr key={`us-sub-${i}`} className="border-t">
                                  <td className="p-2">{item.task}</td>
                                  <td className="p-2">{item.owner}</td>
                                  <td className="p-2">{item.deadline}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          ))
        )}
      </div>
    </div>
  );
}


// ==================== MAIN COMPONENT ====================
export default function UsSection({ projectId, defaultTab }) {
  const { user, isAdmin } = useAuth();
  // Per-user tab memory — returning users land on the tab they used last.
  const userKey = user?.id || 'anon';
  const [activeTab, setActiveTab] = useLayoutPref(`us_tab_${userKey}`, defaultTab || 'directory');

  // Honour explicit prop (from specific sidebar sub-routes like us-threads) — skip when undefined.
  useEffect(() => {
    if (defaultTab) setActiveTab(defaultTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultTab]);

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Collaborative workspace for buyers, residents, and transparent communication with developers.
      </p>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="max-w-full overflow-x-auto">
        <TabsList className="flex flex-wrap justify-start h-auto gap-1 w-full sm:grid sm:grid-cols-5">
          <TabsTrigger value="directory" className="text-xs whitespace-nowrap">
            <Users className="h-3 w-3 mr-1 hidden sm:inline" /> Directory
          </TabsTrigger>
          <TabsTrigger value="correspondence" className="text-xs whitespace-nowrap">
            <Mail className="h-3 w-3 mr-1 hidden sm:inline" /> Correspondence
          </TabsTrigger>
          <TabsTrigger value="notices" className="text-xs whitespace-nowrap">
            <Bell className="h-3 w-3 mr-1 hidden sm:inline" /> Notices
          </TabsTrigger>
          <TabsTrigger value="threads" className="text-xs whitespace-nowrap">
            <MessageSquare className="h-3 w-3 mr-1 hidden sm:inline" /> Threads
          </TabsTrigger>
          <TabsTrigger value="meetings" className="text-xs whitespace-nowrap">
            <Calendar className="h-3 w-3 mr-1 hidden sm:inline" /> Meetings
          </TabsTrigger>
        </TabsList>
        </div>

        <TabsContent value="directory" className="mt-4">
          <ResidentsDirectory projectId={projectId} />
        </TabsContent>

        <TabsContent value="correspondence" className="mt-4">
          <CorrespondenceRepository projectId={projectId} isAdmin={isAdmin} />
        </TabsContent>

        <TabsContent value="notices" className="mt-4">
          <NoticesAgenda projectId={projectId} isAdmin={isAdmin} />
        </TabsContent>

        <TabsContent value="threads" className="mt-4">
          <IssueThreads projectId={projectId} isAdmin={isAdmin} />
        </TabsContent>

        <TabsContent value="meetings" className="mt-4">
          <MeetingsMinutes projectId={projectId} isAdmin={isAdmin} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
